import { SCENEWEAVE_GITHUB_URL } from '../externalLinks';
import { Locale } from '../studioLocale';
import { entryText } from '../entryLocale';
import React from 'react';
import { entryActions, EntryActionId } from '../entryModel';
import ModelKeyControl from './ModelKeyControl';
import './ProjectHub.css';

interface ProjectHubProps {
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
  authenticated: boolean;
  hasDraft: boolean;
  recentRuns: Array<{ id?: string; generation_type?: string; status?: string; created_at?: string }>;
  onAction: (id: EntryActionId) => void;
}

export default function ProjectHub({ locale, onLocaleChange, authenticated, hasDraft, recentRuns, onAction }: ProjectHubProps) {
  const t = (text: string) => entryText(locale, text);
  const actions = entryActions(authenticated, hasDraft);
  const original = actions.find((action) => action.id === 'new-short')!;
  return (
    <main className="project-hub">
      <header className="project-hub__bar">
        <a className="project-hub__brand" href="/">蓦浮 <strong>Morpho</strong><span>AI Narrative Studio</span></a>
        <nav><select aria-label="Language / 语言" value={locale} onChange={(event) => onLocaleChange(event.target.value as Locale)}><option value="zh">中文</option><option value="en">English</option></select>
          <button onClick={() => onAction('novel')}>{t("剧本改编")}</button>
          <button onClick={() => onAction('audio')}>{t("有声剧制作")}</button>
          <button onClick={() => onAction('research')}>{t("呆板化研究")}</button>
          <button onClick={() => onAction('advanced')}>{t("高级画布")}</button>
          <a className="project-hub__external-link" href={SCENEWEAVE_GITHUB_URL} target="_blank" rel="noopener noreferrer">SceneWeave · GitHub ↗</a>
          <span className={`project-hub__status ${authenticated ? 'is-ready' : ''}`}>{authenticated ? t('工作区已连接') : t('访客模式')}</span>
        </nav>
      </header>

      <section className="project-hub__main">
        <div className="project-hub__hero">
          <p className="project-hub__eyebrow">MORPHOR · STORY / AUDIO / RESEARCH</p>
          <h1>{t("导入原著，")}<br />{t("开始改编剧本。")}</h1>
          <p className="project-hub__lead">{t("用织梦读取原著、比较场面试写，调整人物和前后场，完成改编剧本。也可以从头创作原创剧本。生成时使用你的 API Key。")}</p>
          <div className="project-hub__hero-actions">
            <button className="is-primary" onClick={() => onAction('novel')}>{t("剧本改编")}<span>{t("开始改编 →")}</span></button>
            <button onClick={() => onAction(original.id)}>{t("原创短剧")}</button>
          </div>
          <div className="project-hub__proof"><span>{t("游客工作区")}</span><span>{t("自带 API Key")}</span><span>{t("原著证据可追溯")}</span><span>{t("分阶段盲审")}</span></div>
        </div>

        <aside className="project-hub__route-map">
          <p className="project-hub__eyebrow">MODEL ACCESS</p>
          <h2>{t("使用你的模型额度")}</h2>
          <ModelKeyControl locale={locale} />
        </aside>
      </section>

      <section className="project-hub__pillars">
        <button onClick={() => onAction('novel')}><span>01 / ADAPTATION</span><strong>{t("剧本改编")}</strong><p>{t("导入 TXT、Markdown 或 EPUB，分块读完整部原著，比较两种场面写法，再形成完整剧本。")}</p><b>{t("进入改编工作台 →")}</b></button>
        <button onClick={() => onAction('audio')}><span>02 / AUDIO DRAMA</span><strong>{t("有声剧制作")}</strong><p>{t("上传 EPUB 原著，建立全书方案与分集制作项目，并试听作品库。")}</p><b>{t("进入制作与作品库 →")}</b></button>
        <button onClick={() => onAction('research')}><span>03 / RESEARCH</span><strong>{t("剧本呆板化研究")}</strong><p>{t("探索后训练对剧本有效多样性的影响，通过分阶段盲审连接文本与评价证据。")}</p><b>{t("查看研究与盲审 →")}</b></button>
      </section>

      <section className="project-hub__lower">
        <article>
          <div className="project-hub__section-title"><div><p className="project-hub__eyebrow">CONTINUE</p><h2>{t("继续创作")}</h2></div></div>
          <button className="project-hub__resume" disabled={!hasDraft} onClick={() => onAction('resume')}>
            <span><strong>{hasDraft ? t('当前工作区') : t('还没有项目')}</strong><small>{hasDraft ? t('保留节点、连接、生成结果和修改历史') : locale === 'en' ? 'Start an adaptation or a new script.' : actions.find((item) => item.id === 'resume')?.reason}</small></span>
            <b>{hasDraft ? t('继续 →') : t('先新建短剧')}</b>
          </button>
        </article>
        <article>
          <div className="project-hub__section-title"><div><p className="project-hub__eyebrow">RECENT RUNS</p><h2>{t("最近生成")}</h2></div><span>{recentRuns.length} {locale === 'en' ? 'runs' : '条'}</span></div>
          <div className="project-hub__runs">
            {recentRuns.slice(0, 3).map((run) => <div key={run.id}><strong>{run.generation_type || t('生成任务')}</strong><span>{run.status || 'unknown'}</span></div>)}
            {!recentRuns.length && <p>{t("新建项目后，分阶段生成记录会出现在这里。")}</p>}
          </div>
        </article>
        <article>
          <div className="project-hub__section-title"><div><p className="project-hub__eyebrow">ORIGINAL STUDIO</p><h2>{t("原创创作工具")}</h2></div></div>
          <div className="project-hub__original-actions"><button onClick={() => onAction('new-short')}>{t("竖屏短剧")}</button><button onClick={() => onAction('new-film')}>{t("电影剧本")}</button><button onClick={() => onAction('advanced')}>{t("高级画布")}</button><a className="project-hub__external-link" href={SCENEWEAVE_GITHUB_URL} target="_blank" rel="noopener noreferrer">SceneWeave · GitHub ↗</a></div>
        </article>
      </section>
    </main>
  );
}
